import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import { FranchiseFeePayer } from './_types';

export * from './_types';

export const FranchiseFeePayersQueryKey = 'franchise-fee-payers';

export const fetchFranchiseFeePayersQueryKey = (
  params?: FetchFranchiseFeePayersParams
) => [FranchiseFeePayersQueryKey, params];

export interface FetchFranchiseFeePayersParams {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateFranchiseFeePayerParams {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  notes?: string;
  platformIds?: Identifier[];
}

export interface UpdateFranchiseFeePayerParams {
  id: Identifier;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  notes?: string;
  platformIds?: Identifier[];
}

export const fetchFranchiseFeePayers = async (
  options: FetchFranchiseFeePayersParams
) => {
  const { page, perPage, search, sortBy, sortDir } = options;
  const params = {
    page: page || 1,
    per: perPage || 25,
    q: {
      s: sortBy && sortDir ? `${sortBy} ${sortDir}` : undefined,
      user_first_name_or_user_last_name_or_user_email_cont: search || undefined,
    },
  };

  const response = await api.get('/api/v4/hq/franchise_fee_payers', { params });
  return {
    franchiseFeePayers: camelcaseKeys(
      response.data.franchise_fee_payers
    ) as FranchiseFeePayer[],
    total: response.data.meta.total,
  };
};

export const fetchFranchiseFeePayer = async (
  id: Identifier
): Promise<FranchiseFeePayer> => {
  const response = await api.get(`/api/v4/hq/franchise_fee_payers/${id}`);
  return camelcaseKeys(response.data) as FranchiseFeePayer;
};

export const createFranchiseFeePayer = async (
  params: CreateFranchiseFeePayerParams
): Promise<FranchiseFeePayer> => {
  const response = await api.post('/api/v4/hq/franchise_fee_payers', {
    franchise_fee_payer: decamelizeKeys(params, { deep: true }),
  });
  return camelcaseKeys(response.data) as FranchiseFeePayer;
};

export const updateFranchiseFeePayer = async (
  params: UpdateFranchiseFeePayerParams
): Promise<FranchiseFeePayer> => {
  const { id, ...updateParams } = params;
  const response = await api.put(`/api/v4/hq/franchise_fee_payers/${id}`, {
    franchise_fee_payer: decamelizeKeys(updateParams, { deep: true }),
  });
  return camelcaseKeys(response.data) as FranchiseFeePayer;
};

export const deleteFranchiseFeePayer = async (
  id: Identifier
): Promise<void> => {
  await api.delete(`/api/v4/hq/franchise_fee_payers/${id}`);
};

export const fetchAssignedPlatformIds = async (): Promise<number[]> => {
  const response = await api.get(
    '/api/v4/hq/franchise_fee_payers/assigned_platform_ids'
  );
  return response.data;
};

export const assignedPlatformIdsQueryKey = () => ['assigned-platform-ids'];
